<template>
    <v-container fluid fill-height>
        <v-row>
            <v-img :src="stockImage2" width="100%" id="loginImg"></v-img>
        </v-row>

        <v-row justify="center" align="center">
            <v-col cols="12" sm="8" md="6" lg="4">
                <v-img :src="consultTedLogo" class="logo"></v-img>

                <v-card-text>
                    <!-- Step 1: Email and Password -->
                    <div v-if="currentStep === 1">
                        <v-form @submit.prevent="sendCode" ref="loginFormRef" class="mt-4">
                            <v-text-field v-model="loginForm.email" label="Email" type="email" outlined dense required :rules="emailRules" class="mb-2"></v-text-field>

                            <v-text-field
                                v-model="loginForm.password"
                                label="Password"
                                :type="showPassword ? 'text' : 'password'"
                                :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                                @click:append-inner="showPassword = !showPassword"
                                outlined
                                dense
                                required
                                :rules="passwordRules"
                                class="mb-3"
                            ></v-text-field>

                            <v-alert v-if="errorMessage" type="error" dense class="mb-3">{{ errorMessage }}</v-alert>

                            <div class="d-flex align-center">
                                <v-btn type="submit" color="secondary" :loading="isSubmitting" large> Login </v-btn>
                                <v-btn v-if="pwa.showInstallPrompt && isChrome" color="primary" large class="ml-2" @click="pwa.onInstallClick">
                                    <v-icon left>mdi-download</v-icon>
                                    Install App
                                </v-btn>
                            </div>
                            <div class="mt-3">
                                <router-link :to="{ name: 'forgot-password' }" class="text-body-2">Forgot password?</router-link>
                            </div>
                        </v-form>
                    </div>

                    <!-- Step 2: 6-Digit Code Entry -->
                    <div v-else-if="currentStep === 2">
                        <v-card-title class="text-h6 text-center">Enter Verification Code</v-card-title>
                        <v-alert color="info" icon="$info" title="Verification Code" variant="tonal" text="Please enter the 6 digit code sent to your phone"></v-alert>
                        <v-form @submit.prevent="handleLogin" ref="codeFormRef" class="mt-4">
                            <div class="d-flex justify-center mb-4">
                                <v-text-field
                                    v-for="(digit, index) in otpDigits"
                                    :key="index"
                                    pattern="[0-9]*"
                                    inputmode="numeric"
                                    type="text"
                                    v-model="otpDigits[index]"
                                    maxlength="1"
                                    outlined
                                    dense
                                    class="otp-input mx-1"
                                    @input="handleInput(index)"
                                    @keydown="handleKeydown($event, index)"
                                    @paste="handlePaste($event, index)"
                                    ref="otpRefs"
                                ></v-text-field>
                            </div>
                            <v-alert v-if="errorMessage" type="error" dense class="mb-3">{{ errorMessage }}</v-alert>
                            <v-btn type="submit" color="secondary" :loading="isSubmitting" large> Login </v-btn>
                            <v-btn variant="text" @click="backToStep1" class="ml-2"> Back </v-btn>
                        </v-form>
                    </div>
                </v-card-text>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup lang="ts">
import { computed, ref, reactive, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { usePatientStore } from '../stores/patient'
import { patientLogin, sendPatientCode } from '../api/auth'
import { useOtpInput } from '../composables/useOtpInput'
import { usePwa } from '@/composables/usePwa'
// @ts-ignore
import consultTedLogo from '../assets/brand_assets/SVG/logo-consultted-secondary-logo.svg'
// @ts-ignore
import stockImage2 from '../assets/stock_images/stock_image_4.jpg'

const router = useRouter()
const patientStore = usePatientStore()
const pwa = usePwa()
const isChrome = ref(false)

function handleBeforeInstall(e: Event) {
    try {
        ;(e as any).preventDefault()
    } catch {}
    pwa.setDeferredPrompt(e)
}

function handleAppInstalled() {
    pwa.handleAppInstalled()
}

// Step management
const currentStep = ref(1)

// OTP input for step 2
const { otpDigits, otpRefs, otp, handlePaste, handleInput, handleKeydown, focusFirst } = useOtpInput()

// Computed properties for reactive access to patient data
const isLoading = patientStore.isLoading
const isSubmitting = ref(false)

// Login form state
const loginForm = reactive({
    email: '',
    password: '',
})

const showPassword = ref(false)
const loginFormRef = ref(null)
const codeFormRef = ref(null)
const errorMessage = ref('')

// Form validation rules
const emailRules = [(v: string) => !!v || 'Email is required', (v: string) => /.+@.+\..+/.test(v) || 'Email must be valid']

const passwordRules = [(v: string) => !!v || 'Password is required', (v: string) => v.length >= 12 || 'Password must be at least 12 characters']

// Check if user is already logged in on mount
onMounted(async () => {
    // Listen for PWA install availability while on the login page
    window.addEventListener('beforeinstallprompt', handleBeforeInstall as EventListener)
    window.addEventListener('appinstalled', handleAppInstalled as EventListener)

    // Detect Chrome (basic UA check) and set flag
    try {
        const ua = navigator.userAgent || ''
        // match Chrome but exclude Edge/Opera/CriOS (iOS Chrome) and other Chromium browsers
        isChrome.value = /Chrome/.test(ua) && !/Edg|OPR|Opera|CriOS|Chromium/.test(ua)
    } catch (e) {
        isChrome.value = false
    }

    try {
        await patientStore.ensurePatient()
        // If we get here, patient is already logged in, redirect to dashboard
        router.replace('/dashboard')
    } catch (error) {
        // Patient login required - stay on login page
        console.log('Patient login required')
    }
})

onBeforeUnmount(() => {
    window.removeEventListener('beforeinstallprompt', handleBeforeInstall as EventListener)
    window.removeEventListener('appinstalled', handleAppInstalled as EventListener)
})

// Send SMS code
const sendCode = async () => {
    try {
        errorMessage.value = ''
        isSubmitting.value = true

        await sendPatientCode(loginForm.email, loginForm.password)

        // Move to step 2
        currentStep.value = 2
        await nextTick()
        await focusFirst()
    } catch (err: any) {
        errorMessage.value = err?.response?.data?.message || 'Failed. Please try again.'
    } finally {
        isSubmitting.value = false
    }
}

// Handle login with code
const handleLogin = async () => {
    try {
        errorMessage.value = ''
        isSubmitting.value = true

        const resp = (await patientLogin(loginForm.email, loginForm.password, otp.value)) as any

        const token = resp.token?.access_token as string | undefined
        if (!token) throw new Error('No access token received')
        localStorage.setItem('patient_token', token)

        // If 2FA is required
        if (resp.two_factor_required) {
            if (resp.two_factor_enabled) {
                // 2FA is enabled, redirect to verification
                router.push('/two-factor')
            } else {
                // 2FA is required but not enabled, redirect to setup
                router.push('/enable-2fa')
            }
            return
        }
        // Load patient into store then redirect
        await patientStore.ensurePatient()
        loginForm.email = ''
        loginForm.password = ''
        router.push('/dashboard')
    } catch (err: any) {
        console.error('Login failed:', err)
        errorMessage.value = err?.response?.data?.message || 'Login failed. Please check your credentials and try again.'
    } finally {
        isSubmitting.value = false
    }
}

// Go back to step 1
const backToStep1 = () => {
    currentStep.value = 1
    errorMessage.value = ''
    otpDigits.value = ['', '', '', '', '', '']
}
</script>

<style scoped>
.v-container {
    min-height: 100vh;
}

.v-card {
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

:deep(.logo .v-img__img) {
    position: relative;
    padding: 20px;
}

.otp-input {
    width: 42px;
    text-align: center;
}

.otp-input :deep(.v-field__input) {
    font-size: 1.5rem;
    font-weight: bold;
    padding: 0.9rem;
}

#loginImg {
    max-height: 300px;
    margin: auto;
}
</style>
